import { apiErrorSchema, loginResponseSchema } from '@plane/shared';
import type { ZodType } from 'zod';

/**
 * `import.meta.env` só existe dentro do Vite.
 *
 * O `?.` é o que permite importar este módulo fora dele — nos testes de
 * montagem, que rodam no Node puro. Sem isso o acesso estoura antes de qualquer
 * componente chegar a renderizar.
 */
const API_URL = import.meta.env?.VITE_API_URL ?? 'http://localhost:3333';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/**
 * Fonte única do token de acesso.
 *
 * Mantido em memória de propósito: `localStorage` é legível por qualquer script
 * da página, e guardar o token ali transformaria um XSS momentâneo em sessão
 * permanente. O preço é perder o token a cada F5 — pago pelo refresh silencioso
 * abaixo, que o reidrata a partir do cookie httpOnly.
 */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Avisado quando a sessão morre de vez, para o painel voltar ao login. */
type SessionEndedListener = () => void;

let onSessionEnded: SessionEndedListener | null = null;

export function setSessionEndedListener(listener: SessionEndedListener | null): void {
  onSessionEnded = listener;
}

/**
 * Renovação em voo, compartilhada.
 *
 * Sem isto, várias requisições recebendo 401 ao mesmo tempo disparariam um
 * refresh cada uma. Todas menos a primeira apresentariam um token já
 * rotacionado, e a API — corretamente — as trataria como corrida. Guardar a
 * promessa faz o painel renovar uma vez só e todo mundo esperar o mesmo
 * resultado.
 */
let refreshInFlight: Promise<string | null> | null = null;

async function requestNewAccessToken(): Promise<string | null> {
  const response = await fetch(`${API_URL}/api/v1/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    const parsed = apiErrorSchema.safeParse(payload);

    // A API sinaliza corrida entre abas com este código: o cookie do navegador
    // já é o novo, gravado pela aba que venceu. Uma segunda tentativa pega o
    // token certo — não é sessão perdida.
    if (parsed.success && parsed.data.error.code === 'SESSION_ROTATED') {
      const retry = await fetch(`${API_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (retry.ok) {
        const corpo = loginResponseSchema.parse(await retry.json());
        setAccessToken(corpo.accessToken);
        return corpo.accessToken;
      }
    }

    return null;
  }

  const corpo = loginResponseSchema.parse(await response.json());
  setAccessToken(corpo.accessToken);

  return corpo.accessToken;
}

/**
 * Renova o token de acesso a partir do cookie httpOnly.
 *
 * Devolve `null` quando não há sessão recuperável — o chamador decide se isso
 * significa ir para o login ou apenas seguir deslogado.
 */
export function refreshAccessToken(): Promise<string | null> {
  refreshInFlight ??= requestNewAccessToken()
    .catch(() => null)
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

/**
 * `content-type: application/json` só entra quando há corpo.
 *
 * O Fastify recusa com 400 `FST_ERR_CTP_EMPTY_JSON_BODY` um POST que declara
 * corpo JSON e não manda nenhum — que é o caso de `/auth/refresh` e
 * `/auth/logout`. Declarar o cabeçalho por reflexo quebraria as duas rotas.
 */
function buildHeaders(init: RequestInit | undefined, token: string | null): HeadersInit {
  return {
    ...(init?.body === undefined ? {} : { 'content-type': 'application/json' }),
    ...(token ? { authorization: `Bearer ${token}` } : {}),
    ...init?.headers,
  };
}

async function toApiError(response: Response): Promise<ApiRequestError> {
  const payload: unknown = await response.json().catch(() => null);
  const parsed = apiErrorSchema.safeParse(payload);

  return new ApiRequestError(
    response.status,
    parsed.success ? parsed.data.error.code : 'UNKNOWN_ERROR',
    parsed.success ? parsed.data.error.message : `Falha na requisição (${response.status}).`,
  );
}

/**
 * Requisição autenticada, sem interpretar o corpo.
 *
 * Em caso de 401, tenta renovar a sessão uma única vez e repete a requisição.
 * Se a renovação falhar, a sessão acabou de verdade e o painel é avisado.
 */
async function enviarAutenticado(path: string, init?: RequestInit): Promise<Response> {
  const enviar = (token: string | null): Promise<Response> =>
    fetch(`${API_URL}${path}`, {
      ...init,
      credentials: 'include',
      headers: buildHeaders(init, token),
    });

  let response = await enviar(accessToken);

  if (response.status === 401) {
    const novo = await refreshAccessToken();

    if (novo) {
      response = await enviar(novo);
    } else {
      setAccessToken(null);
      onSessionEnded?.();
    }
  }

  return response;
}

/** Requisição autenticada à API, com validação do corpo em runtime. */
export async function apiRequest<T>(
  path: string,
  schema: ZodType<T>,
  init?: RequestInit,
): Promise<T> {
  const response = await enviarAutenticado(path, init);

  if (!response.ok) throw await toApiError(response);

  // 204 não tem corpo; `schema` costuma ser `z.void()` nesses casos.
  if (response.status === 204) return schema.parse(undefined);

  return schema.parse(await response.json());
}

/**
 * Baixa bytes de uma rota autenticada.
 *
 * Existe por causa das fotos: `<img src>` não tem como mandar o
 * `Authorization`, e sem token a API responde 404 em foto de rascunho — que é
 * o comportamento certo, e é justamente o que o painel precisa exibir. Buscar
 * aqui e renderizar por `object URL` resolve sem afrouxar a regra na API.
 *
 * O cache HTTP continua valendo: a rota manda `immutable`, então a segunda
 * visita não sai do disco.
 */
export async function apiRequestBytes(path: string): Promise<Blob> {
  const response = await enviarAutenticado(path);

  if (!response.ok) throw await toApiError(response);

  return response.blob();
}

/** Base da API, para as raras chamadas que não passam por `apiRequest`. */
export const apiBaseUrl = API_URL;
